import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasOnError, HasOnSuccess } from "@insite/client-framework/HandlerCreator";

import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";

import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";

import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";

import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";

import { LinkPresentationProps } from "@insite/mobius/Link/Link";

import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import BlueFileImage from "@tSchaedlerYesco/Icons/BlueFileImage";
import GreyFileImage from "@tSchaedlerYesco/Icons/GreyFileImage";
import CustomApplicationState from "@tSchaedlerYesco/Store/CustomApplicationState";
import updateAccountShowProductBrandImagesHandler from "@tSchaedlerYesco/Store/Data/ProductBrandImage/Handler/AccountShowProductBrandImageHandler";
import React, { ChangeEvent, useContext, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapDispatchToProps = { updateAccountShowProductBrandImagesHandler };

const mapStateToProps = (state: CustomApplicationState, appstate: ApplicationState) => {
    return {
        userName: state.context.session.userName,
        guestUserHideShowImages: state.context.settings.properties.guestUserHideShowImages,
        showProductBrandImages: state.tData.showProductImagesFlag.showProductBrandImages,
    };
};
type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface AccountSettingsUserInformationStyles {
    userInformationTitle: TypographyProps;
    userInformationGridItem?: GridItemProps;
    changePasswordLink?: LinkPresentationProps;
    wrapper?: InjectableCss;
    listViewClickable?: ClickablePresentationProps;
    listViewIcon?: IconPresentationProps;
    BlueViewIcon?: IconPresentationProps;
    GreyViewIcon?: IconPresentationProps;
    iconWrapper?: InjectableCss;
}

export const userInformationStyles: AccountSettingsUserInformationStyles = {
    userInformationGridItem: {
        width: 12,
        css: css``,
    },
    changePasswordLink: {
        css: css`
            margin-left: 10px;
        `,
    },
    userInformationTitle: {
        variant: "h2",
    },
    wrapper: {
        css: css`
            display: flex;
            padding-top: 5px;
            float: right;
        `,
    },
    listViewClickable: {
        css: css`
            margin-right: 40px;
        `,
    },
    BlueViewIcon: {
        src: BlueFileImage,
        size: 26,
        css: css`
            margin-left: 20px;
        `,
    },
    GreyViewIcon: {
        src: GreyFileImage,
        size: 26,
        css: css`
            margin-left: 20px;
        `,
    },
    iconWrapper: {
        css: css`
            .hover-text {
                position: absolute;
                top: -25px; /* Adjust this as needed */
                left: 50%;
                transform: translateX(-50%);
                visibility: hidden;
                opacity: 0;
                background-color: black;
                color: white;
                padding: 5px;
                border-radius: 3px;
                font-size: 12px;
                transition: opacity 0.3s ease;
            }

            &:hover .hover-text {
                visibility: visible;
                opacity: 1;
                width: max-content;
                cursor: pointer;
            }
        `,
    },
};

const styles = userInformationStyles;

const guestToggleFilter = ({
    userName,
    guestUserHideShowImages,
    showProductBrandImages,
    updateAccountShowProductBrandImagesHandler,
}: Props) => {
    if (userName) {
        return null;
    }

    const [guestHideShowImages, setGuestHideShowImages] = useState(showProductBrandImages);

    // console.log("guestUserHideShowImages", guestUserHideShowImages);

    const ShowImagesViewHandler = () => {
        setGuestHideShowImages(true);
        updateAccountShowProductBrandImagesHandler({ showProductBrandImages: true });
        toasterContext.addToast({ body: translate("Settings updated"), messageType: "success" });
    };
    const DontShowImagesViewHandler = () => {
        setGuestHideShowImages(false);
        updateAccountShowProductBrandImagesHandler({ showProductBrandImages: false });
        toasterContext.addToast({ body: translate("Settings updated"), messageType: "success" });
    };

    const toasterContext = useContext(ToasterContext);

    return (
        <>
            <StyledWrapper {...styles.wrapper}>
                {guestHideShowImages === true && (
                    <StyledWrapper {...styles.iconWrapper}>
                        <Clickable
                            data-test-selector="viewSelectList"
                            {...styles.listViewClickable}
                            onClick={DontShowImagesViewHandler}
                            style={{ position: "relative" }} // For positioning the tooltip
                        >
                            <Icon {...styles.BlueViewIcon} />
                            <div className="icon-container">
                                <span className="hover-text">Hide/Show Images </span>
                            </div>
                        </Clickable>
                    </StyledWrapper>
                )}
                {guestHideShowImages === false && (
                    <StyledWrapper {...styles.iconWrapper}>
                        <Clickable
                            data-test-selector="viewSelectList"
                            {...styles.listViewClickable}
                            onClick={ShowImagesViewHandler}
                            style={{ position: "relative" }} // For positioning the tooltip
                        >
                            <Icon {...styles.GreyViewIcon} />
                            <div className="icon-container">
                                <span className="hover-text">Hide/Show Images </span>
                            </div>
                        </Clickable>
                    </StyledWrapper>
                )}
            </StyledWrapper>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(guestToggleFilter),
    definition: {
        group: "Product List",
        displayName: "Guest Toggle  Img Filter",
    },
};

export default widgetModule;
